/**
 * Canvas + context WebGL2 cho MediaPipe, kèm chẩn đoán — Prompt.md §5, §31.
 *
 * WebGL2 là điều kiện sống của MediaPipe web, **không chỉ của GPU delegate**. Đọc bundle
 * tasks-vision 1.0.1: GraphRunner luôn dựng một canvas GL để đẩy frame video vào graph
 * (`this.i.canvas = e ?? (Ph() ? new OffscreenCanvas(1,1) : document.createElement("canvas"))`),
 * kể cả khi `baseOptions.delegate === "CPU"`. GL chết thì cả hai delegate đều chết.
 *
 * Vì sao phải tự kiểm tra thay vì để MediaPipe tự phát hiện: nó không kiểm tra gì cả, nó gọi thẳng
 * vào GL từ trong wasm. Context hỏng → Emscripten `abort()` → đúng dòng người dùng thấy trên máy
 * thật: `Aborted(). Build with -sASSERTIONS for more info.` — không tên hàm, không stack, và xảy ra
 * ở khâu **inference** nên trông như "app chạy rồi mới hỏng".
 *
 * Nguyên tắc của file này: **kiểm tra đúng cái object sẽ dùng thật, không kiểm tra bản sao.**
 * Bản trước tách làm hai — probe trên một canvas rời DOM, rồi giao cho MediaPipe một canvas khác
 * gắn DOM — và thế là kết quả đo nói về một object không ai dùng. Trên iOS đó không phải chi tiết
 * vụn: canvas rời DOM và canvas gắn DOM bị WebKit đối xử khác nhau, mà số context WebGL sống đồng
 * thời cũng có hạn nên tạo thêm một context chỉ để đo là tự làm tăng khả năng context bị mất.
 */

/** Ngưỡng thấp nhất còn dùng được: model lite chạy ở 256×256, 2048 là mức thiết bị 2015 cũng có. */
const MIN_TEXTURE_SIZE = 2048;

/**
 * Extension cần cho float render target của GPU delegate (TFLite GL dùng float texture cho tensor).
 *
 * Thiếu chúng thì **không** kết luận là máy hỏng — chỉ là GPU delegate không đáng tin, và hạ xuống
 * CPU vẫn cho kết quả đúng (chậm hơn, `InferenceLoop` tự hạ FPS theo p95). Đổi chậm lấy đúng.
 */
const FLOAT_TARGET_EXTENSIONS = ["EXT_color_buffer_float", "OES_texture_float_linear"] as const;

export type Webgl2Health =
  | {
      ok: true;
      maxTextureSize: number;
      /** `false` → còn dùng được nhưng phải đi CPU delegate. */
      floatRenderTarget: boolean;
      /** Câu tóm tắt cho Debug Mode (§25). */
      note: string;
    }
  | { ok: false; reason: string };

export interface VisionGlSurface {
  /** Truyền vào `PoseLandmarker.createFromOptions({ canvas })`. */
  canvas: HTMLCanvasElement;
  /** Đo trên chính context của `canvas` này, không phải trên một context khác. */
  health: Webgl2Health;
  /** Context mất **sau** khi đã init (sự kiện `webglcontextlost`) — khác với mất ngay lúc tạo. */
  isLost: () => boolean;
  remove: () => void;
}

export interface GlEnvironment {
  open: () => VisionGlSurface;
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Tách riêng khỏi việc tạo canvas để test được mà không cần `document`. */
export function inspectWebgl2Health(gl: WebGL2RenderingContext | null): Webgl2Health {
  if (!gl) return { ok: false, reason: 'getContext("webgl2") trả null — trình duyệt tắt WebGL2' };
  if (gl.isContextLost()) return { ok: false, reason: "WebGL2 context mất ngay khi vừa tạo" };

  /*
   * `getParameter` trả null là ca đã gặp trên iPhone iOS 18.7.2 thật (ios-probe.html báo
   * "maxTexture null" cùng với mọi `getExtension` → null, trong khi `getContext` vẫn trả object).
   * Theo spec WebGL, một context đã mất trả null cho **mọi** truy vấn — nên đây là context chết
   * được ngụy trang thành context sống, và là thứ làm MediaPipe abort trong wasm. Không tin
   * `isContextLost()` một mình là vì vậy: nó có thể còn trả false trong khi context đã không dùng được.
   */
  const maxTextureSize: unknown = gl.getParameter(gl.MAX_TEXTURE_SIZE);
  if (typeof maxTextureSize !== "number" || Number.isNaN(maxTextureSize)) {
    return {
      ok: false,
      reason: `MAX_TEXTURE_SIZE = ${String(maxTextureSize)} (context không trả lời được → coi như đã mất)`,
    };
  }
  if (maxTextureSize < MIN_TEXTURE_SIZE) {
    return { ok: false, reason: `MAX_TEXTURE_SIZE chỉ ${String(maxTextureSize)}` };
  }

  const missing = FLOAT_TARGET_EXTENSIONS.filter((name) => gl.getExtension(name) === null);
  return {
    ok: true,
    maxTextureSize,
    floatRenderTarget: missing.length === 0,
    note:
      missing.length === 0
        ? `WebGL2 đủ điều kiện (MAX_TEXTURE_SIZE ${String(maxTextureSize)})`
        : `thiếu ${missing.join(", ")}`,
  };
}

/**
 * Canvas GL của riêng mình để truyền vào `createFromOptions({ canvas })`.
 *
 * Không truyền thì tasks-vision tự chọn: hàm `Ph()` trong bundle là
 * `typeof OffscreenCanvas !== "undefined" && (!isSafari || Version >= 17)`, nên **Safari 17+ đi
 * nhánh `new OffscreenCanvas(1, 1)`** — nhánh ít được test nhất trên iOS.
 *
 * 1×1 ở góc màn hình chứ không `display: none`, không `opacity: 0`, không nằm ngoài viewport:
 * canvas bị ẩn là dạng WebKit thu hồi GPU resource sớm nhất. Một pixel thì mắt không thấy, mà
 * `position: fixed` thì không đẩy layout.
 *
 * (Đã kiểm trên iPhone iOS 18.7.2: cách đặt canvas **không** cứu được máy đã tắt WebGL — canvas
 * gắn DOM, canvas rời DOM, `OffscreenCanvas` và cả WebGL1 đều trả `CONTEXT_LOST_WEBGL` như nhau.
 * Nên đây chỉ là cách đặt an toàn nhất, không phải một cơ chế fallback.)
 */
export function openVisionGlSurface(): VisionGlSurface {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  canvas.setAttribute("aria-hidden", "true");
  canvas.style.cssText = "position:fixed;right:0;bottom:0;width:1px;height:1px;pointer-events:none";
  document.body.appendChild(canvas);

  // Context có thể mất giữa phiên (Safari khởi động lại GPU process, máy nóng, đổi tab). Bắt sự
  // kiện để lần sau `Aborted()` có kèm câu giải thích, thay vì chỉ một dòng abort trần.
  let lostAfterInit = false;
  canvas.addEventListener("webglcontextlost", () => {
    lostAfterInit = true;
  });

  let gl: WebGL2RenderingContext | null = null;
  let health: Webgl2Health;
  try {
    // KHÔNG truyền attribute nào: MediaPipe gọi `canvas.getContext("webgl2")` trần (hàm `Gh` trong
    // bundle), và `getContext` cùng type trả về **đúng cùng một** context object — nên context
    // được đo ở đây chính là context nó sẽ dùng. Truyền attribute khác đi thì hai bên lệch nhau.
    gl = canvas.getContext("webgl2");
    health = inspectWebgl2Health(gl);
  } catch (err) {
    health = { ok: false, reason: `getContext("webgl2") throw — ${describe(err)}` };
  }

  return {
    canvas,
    health,
    isLost: () => lostAfterInit || gl === null || gl.isContextLost(),
    remove: () => {
      // Nhả GPU resource ngay chứ không đợi GC: iOS Safari giới hạn số context sống rất chặt, và
      // chính giới hạn đó là một trong các cách context "mất ngay khi vừa tạo".
      gl?.getExtension("WEBGL_lose_context")?.loseContext();
      canvas.remove();
    },
  };
}

export const defaultGlEnvironment: GlEnvironment = { open: openVisionGlSurface };
